import React, { Component } from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { navigateTo } from 'gatsby-link'
import { Motion, spring } from 'react-motion'

import {
  setModalStatus,
  setRoomsCleared,
  setGameFinished,
  setSelectedRoom,
  setStartButtonUrl,
} from '../modules/gameProgress'
import { submitQuizAnswer, closeQuizModal } from '../modules/quizzes'

import { theme, media } from '../theme'
import Heading from './Heading'
import Copy from './Copy'
import Button from './Button'
import {
  getQuizSubmitButtonText,
  isLastRoom,
  selectRandomRoom,
  delay,
} from '../helpers'

import CharacterKeoniTipImage from '../assets/images/character--keoni-tip@3x.png'
import ShakaImage from '../assets/images/shaka.png'

const QUIZ_STATE = {
  INITIAL: 'initial',
  CORRECT: 'correct',
  INCORRECT: 'incorrect',
}

const GUESS_STATE = {
  INITIAL: 'guess-initial',
  CORRECT: 'guess-correct',
  INCORRECT: 'guess-incorrect',
}

const CHOICES = ['a', 'b', 'c', 'd']

const Container = styled.div`
  display: ${(p) => (p.isOpen ? 'flex' : 'none')};
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  z-index: 11;
  padding: 0 30px;
`

const Content = styled.div`
  background: linear-gradient(0deg, ${theme.color.Tip.bg.join(',')});
  border-radius: ${theme.spacing(-1)};
  font-family: ${theme.fontFamily.primary};
  isolation: isolate;
  width: 100%;
  align-self: center;
  padding: 24px 0;
  display: flex;
  flex-direction: column;
  position: relative;

  ${media.greaterThan('medium')`
    width: 600px;
    margin: 0 auto;
    padding: 50px 0;
  `};
`

const ScrollContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  max-height: 50vh;
  padding: 0 24px;
  margin-top: 1.25rem;

  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  ${media.greaterThan('medium')`
    padding: 0 50px;
    max-height: none;
    overflow-y: hidden;
    margin-top: 0;
  `};
`

const Overlay = styled.div`
  background-color: rgba(0, 0, 0, 0.6);
  pointer-events: auto;
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  height: 100%;
  width: 100%;
`

const Character = styled.div`
  width: 100px;
  align-self: center;
  margin-top: calc((24px + 50px) * -1);

  img {
    height: 100%;
    max-width: 100%;
    object-fit: contain;
  }

  ${media.greaterThan('medium')`
    display: none; 
  `}
`

const QuizHeading = styled(Heading)`
  margin-bottom: 0;
  font-size: 2rem;
  text-transform: none;

  color: ${(p) =>
    [QUIZ_STATE.CORRECT].includes(p.quizState)
      ? p.theme.color.limeGreen
      : p.quizState === QUIZ_STATE.INCORRECT
      ? '#e75c5c'
      : undefined};

  ${media.greaterThan('medium')`
    font-size: 2.5rem;
  `}
`

const QuizCopy = styled(Copy)`
  margin-top: 0.25rem;
`

const MultipleChoice = styled.div`
  display: grid;
  gap: 0.75rem;
  margin-top: 1.5rem;

  ${media.greaterThan('medium')`
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
    gap: 1rem;
    margin-top: 2rem;
  `}
`

const OptionButton = styled.button`
  display: flex;
  cursor: pointer;
  border: 0;
  background: none;
  padding: 0;

  &:disabled {
    pointer-events: none;
  }
`

const Letter = styled.div`
  isolation: isolate;
  border-radius: 50%;
  border: 0.2rem solid ${theme.color.Button.border};
  background: ${(p) =>
    p.guessState === GUESS_STATE.INITIAL
      ? `linear-gradient(to bottom, ${theme.color.Button.bg.join(',')})`
      : p.guessState === GUESS_STATE.INCORRECT
      ? // prettier-ignore
        `linear-gradient(to bottom, ${theme.color.Hint.bg.join(',')}) !important`
      : // prettier-ignore
        `linear-gradient(to bottom, ${theme.color.Button.bgActive.join(',')}) !important`};

  font-family: ${(p) => p.theme.fontFamily.primary};
  color: ${(p) => p.theme.color.Button.copy};
  font-size: ${(p) => p.theme.fontSize(2)};

  width: 48px;
  height: 48px;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  align-self: center;

  &:hover {
    background: linear-gradient(
      to bottom,
      ${(props) =>
        props.theme.color.Button.bgHover
          ? props.theme.color.Button.bgHover.join(',')
          : props.theme.color.Button.bg.join(',')}
    );
  }
`

const StyledShaka = styled.div`
  width: ${(p) => p.width || '68px'};
  position: ${(p) => p.position};
  right: ${(p) => p.right};
  bottom: ${(p) => p.bottom};

  img {
    height: 100%;
    max-width: 100%;
    object-fit: contain;
  }
`

const QuizButton = styled(Button)`
  align-self: center;
  min-width: 160px;
  margin-bottom: calc((24px + 28px) * -1);
  margin-top: 1.5rem;
  cursor: pointer;

  ${media.greaterThan('medium')`
    margin-bottom: calc((50px + 28px) * -1);
    margin-top: 2rem;
  `}
`

const TextContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;

  background-color: #032e39;
  border-radius: 8px;
  margin-left: -25px;
  padding: 6px 20px 6px 37px;
  height: 100%;
`

const Shaka = (props) => (
  <StyledShaka {...props}>
    <img
      src={ShakaImage}
      alt=""
      loading="lazy"
      decoding="async"
      width={366}
      height={336}
    />
  </StyledShaka>
)

const OptionCopy = styled(Copy)`
  color: ${(p) =>
    p.guessState === GUESS_STATE.CORRECT ? theme.color.limeGreen : undefined};
  opacity: ${(p) => (p.guessState === GUESS_STATE.INCORRECT ? 0.5 : undefined)};
`

const Option = ({
  label,
  value,
  guessState = GUESS_STATE.INITIAL,
  quizState,
  ...props
}) => (
  <OptionButton
    disabled={
      guessState === GUESS_STATE.INCORRECT || quizState === QUIZ_STATE.CORRECT
    }
    {...props}
  >
    <Letter guessState={guessState}>{value.toUpperCase()}</Letter>

    <TextContainer>
      <OptionCopy last textAlign="left" guessState={guessState}>
        {label}
      </OptionCopy>
    </TextContainer>
  </OptionButton>
)

class QuizModal extends Component {
  static initialState = {
    x: 0,
    quizState: QUIZ_STATE.INITIAL,
    incorrectGuesses: [],
    correctGuesses: [],
    isSubmitting: false,
  }

  constructor(props) {
    super(props)

    this.handleClick = this.handleClick.bind(this)
    this.handleContinue = this.handleContinue.bind(this)
    this.resetModal = this.resetModal.bind(this)
    this.shake = this.shake.bind(this)
    this.finalize = this.finalize.bind(this)

    this.state = QuizModal.initialState
  }

  resetModal() {
    this.setState(QuizModal.initialState)
    this.props.closeQuizModal()
  }

  async shake() {
    this.setState({ x: -35, hasAnsweredIncorrectly: true })
    await delay(75)
    this.setState({ x: 35 })
    await delay(75)
    this.setState({ x: 0 })
  }

  handleClick(guess) {
    if (this.state.quizState === QUIZ_STATE.CORRECT) return

    const { answer } = this.props

    if (!answer.includes(guess)) {
      this.setState({
        quizState: QUIZ_STATE.INCORRECT,
        incorrectGuesses: [...this.state.incorrectGuesses, guess],
      })
      this.shake()

      return
    }

    const correctGuesses = [...this.state.correctGuesses, guess]
    const isDone = correctGuesses.length === answer.length

    this.setState({
      quizState: isDone ? QUIZ_STATE.CORRECT : QUIZ_STATE.INITIAL,
      correctGuesses: [...this.state.correctGuesses, guess],
    })
  }

  finalize() {
    const {
      setModalStatus,
      submitQuizAnswer,
      selectedRoomId,
      roomsCleared,
      setGameFinished,
      setSelectedRoom,
      setStartButtonUrl,
      setRoomsCleared,
    } = this.props

    const hasClearedRoom = roomsCleared.includes(selectedRoomId)
    if (!hasClearedRoom) {
      setRoomsCleared([...roomsCleared, selectedRoomId])
    }

    submitQuizAnswer({ roomId: selectedRoomId })
    this.resetModal()

    if (isLastRoom(roomsCleared, selectedRoomId)) {
      setGameFinished(true)
      setModalStatus(false)

      return navigateTo('/aloha')
    }

    selectRandomRoom(
      [...roomsCleared, selectedRoomId],
      setSelectedRoom,
      setStartButtonUrl,
    )
    setModalStatus(true)
  }

  handleContinue() {
    this.finalize()
  }

  render() {
    const { isActive, question, options, roomsCleared, selectedRoomId } =
      this.props
    const { quizState, incorrectGuesses, correctGuesses } = this.state

    return (
      <Container isOpen={isActive}>
        <Overlay />

        <Motion
          defaultStyle={{ x: 0 }}
          style={{ x: spring(this.state.x, { stiffness: 300 }) }}
        >
          {(style) => (
            <Content style={{ transform: `translateX(${style.x}px)` }}>
              <Character>
                <img src={CharacterKeoniTipImage} width={303} height={323} />
              </Character>

              <ScrollContainer>
                <QuizHeading
                  level={1}
                  textAlign="left"
                  quizState={quizState}
                  first
                >
                  {quizState === QUIZ_STATE.INITIAL && 'POP QUIZ!'}
                  {quizState === QUIZ_STATE.CORRECT &&
                    "CORRECT! You've earned a shaka!"}
                  {quizState === QUIZ_STATE.INCORRECT && 'OOPS! Try Again.'}
                </QuizHeading>

                <QuizCopy textAlign="left" last>
                  {question}
                </QuizCopy>

                <MultipleChoice>
                  {CHOICES.map((option) => {
                    const label = options[option]
                    if (!label) return null

                    return (
                      <Option
                        key={option}
                        label={label}
                        value={option}
                        guessState={
                          incorrectGuesses.includes(option)
                            ? GUESS_STATE.INCORRECT
                            : correctGuesses.includes(option)
                            ? GUESS_STATE.CORRECT
                            : GUESS_STATE.INITIAL
                        }
                        quizState={quizState}
                        onClick={() => this.handleClick(option)}
                      />
                    )
                  })}
                </MultipleChoice>
              </ScrollContainer>

              {quizState === QUIZ_STATE.CORRECT && (
                <Shaka position="absolute" right="15px" bottom="15px" />
              )}

              {quizState === QUIZ_STATE.CORRECT && (
                <QuizButton useButtonTag onClick={this.handleContinue}>
                  {getQuizSubmitButtonText({
                    roomsCleared,
                    roomId: selectedRoomId,
                  })}
                </QuizButton>
              )}
            </Content>
          )}
        </Motion>
      </Container>
    )
  }
}

const mapStateToProps = (state) => ({
  isActive: state.quizzes.isActive,
  question: state.quizzes.question,
  answer: state.quizzes.answer,
  options: state.quizzes.options,
  selectedRoomId: state.gameProgress.selectedRoom,
  roomsCleared: state.gameProgress.roomsCleared,
  isOver18: state.player.isOver18,
  clearedQuizRoomIds: state.quizzes.clearedQuizRoomIds,
  firstName: state.player.firstName,
  lastName: state.player.lastName,
  zipCode: state.player.zipCode,
  email: state.player.email,
})

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      setModalStatus,
      setGameFinished,
      setRoomsCleared,
      setSelectedRoom,
      setStartButtonUrl,
      submitQuizAnswer,
      closeQuizModal,
    },
    dispatch,
  )

const QuizModalConnected = connect(
  mapStateToProps,
  mapDispatchToProps,
)(QuizModal)
export default QuizModalConnected



// WEBPACK FOOTER //
// ./src/components/QuizModal.js